// Component to display group(s) of Operators for a Job as a Card footer
import { useEffect, useState } from 'react';

import {
  ExclamationTriangleIcon,
  PlusIcon,
  UserPlusIcon,
} from '@heroicons/react/16/solid';
import clsx from 'clsx';
import { useDrop } from 'react-dnd';

import { compact, groupBy, map, noop, orderBy, times } from 'lodash';

import JobAssignmentInfo from '@/components/Scheduler/SchedulerMain/JobCard/JobGroups/JobAssignmentInfo';

import { useAuthContext } from '@/context/AuthContext';
import {
  parseJobAssignmentsBasedOnOperationsRecord,
  useEditSchedulerContext,
} from '@/context/EditSchedulerContext';

import { firestore } from '@isd/lib/firebase';
import {
  findParentJobAssignment,
  getCanOperatorsBeAddedToJob,
  getCanUserManageJobAssignmentsGroup,
  getJobStartDatetime,
} from '@isd/lib/firebase/db/helpers';
import { JobAssignmentDoc, JobDoc } from '@isd/lib/firebase/db/metaTypes';
import { jobsAssignmentsByJobQuery } from '@isd/lib/firebase/db/queries';

import { reactAppJobEditRoute, reactAppUrl } from '@/env';
import { replaceParamsInRoute } from '@isd/lib/helpers/functions';

export default function JobGroups({ jobDoc }: { jobDoc: JobDoc }) {
  const [originalJobAssignments, setOriginalJobAssignments] = useState<
    JobAssignmentDoc[] | null
  >(null);
  const [jobAssignments, setJobAssignments] = useState<JobAssignmentDoc[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [canOperatorsBeAdded, setCanOperatorsBeAdded] = useState(false);
  const { userDoc } = useAuthContext();
  const { selectedJob, onChangeSelectedJobAssignments, operationsRecordList } =
    useEditSchedulerContext();

  useEffect(() => {
    const unSubscribe = !userDoc
      ? noop
      : firestore.onSnapshot(
          jobsAssignmentsByJobQuery({ userDoc, jobId: jobDoc.id }),
          (querySnapshot) => {
            setOriginalJobAssignments(
              firestore.filterSoftDeletedDocs(querySnapshot.docs)
            );
          }
        );

    getCanOperatorsBeAddedToJob(jobDoc).then(setCanOperatorsBeAdded);

    return unSubscribe;
  }, []);

  useEffect(() => {
    if (!originalJobAssignments) return;
    const parsedJobAssignments = parseJobAssignmentsBasedOnOperationsRecord(
      jobDoc.id,
      originalJobAssignments,
      operationsRecordList
    ) as JobAssignmentDoc[];
    setJobAssignments(parsedJobAssignments);
    setIsLoading(false);
  }, [operationsRecordList, originalJobAssignments]);

  // Update selected JobAssignments in EditSchedulerContext
  useEffect(() => {
    if (selectedJob?.id === jobDoc.id && !!originalJobAssignments) {
      onChangeSelectedJobAssignments(originalJobAssignments);
    }
  }, [originalJobAssignments?.length, selectedJob]);

  const jobData = jobDoc.data();
  const groupsCount = jobData?.groupsOfOperators.length || 0;
  // Only render leaf Job Assignments
  const leafJobAssignments = jobAssignments.filter(
    (jobAssignmentDoc) => !!jobAssignmentDoc.get('isLeafNode')
  );
  const jobAssignmentsByGroup = groupBy(
    leafJobAssignments,
    (jobAssignmentDoc) => jobAssignmentDoc.data()?.groupIndex
  );

  return (
    <div
      className={clsx(
        'job-groups rounded-b-xl p-1 pb-2',
        groupsCount > 1 && 'bg-accent-dark'
      )}
    >
      {isLoading ? (
        <>
          {times(2, (index) => (
            <JobAssignmentLoading key={index} />
          ))}
        </>
      ) : groupsCount > 0 ? (
        map(jobData?.groupsOfOperators, (_, groupIndex) => {
          const jobAssignmentsAtGroup = jobAssignmentsByGroup[groupIndex] || [];
          // Check that if group is empty then User can assign operators to it otherwise don't display it
          if (
            !jobAssignmentsAtGroup.length &&
            !!userDoc &&
            originalJobAssignments &&
            !getCanUserManageJobAssignmentsGroup({
              userDoc,
              jobDoc,
              jobAssignments: originalJobAssignments,
              groupIndex,
            })
          ) {
            return null;
          }
          return (
            <JobAssignmentsGroup
              key={groupIndex}
              jobAssignmentsAtGroup={jobAssignmentsAtGroup}
              groupIndex={groupIndex}
              jobDoc={jobDoc}
              groupsCount={groupsCount}
              canOperatorsBeAdded={canOperatorsBeAdded}
              originalJobAssignments={originalJobAssignments || []}
            />
          );
        })
      ) : (
        <EmptyJobAssignments jobDoc={jobDoc} />
      )}
    </div>
  );
}

const onNavigateToJobForm = (jobDoc: JobDoc) => {
  const jobId = jobDoc.id;
  const editJobUrl = `${reactAppUrl}${replaceParamsInRoute(reactAppJobEditRoute, { jobId })}`;
  window.open(editJobUrl);
};

function EmptyJobAssignments({ jobDoc }: { jobDoc: JobDoc }) {
  return (
    <div
      className="add-operators-link clickable bg-base-100 flex justify-center rounded-2xl p-3 text-center hover:underline"
      onClick={() => onNavigateToJobForm(jobDoc)}
    >
      <PlusIcon className="w-5" />
      <div>Add Operators</div>
    </div>
  );
}

function JobAssignmentLoading() {
  return <div className="skeleton bg-base-200 mt-1 h-5 w-full"></div>;
}

function getIsSameUserAssignedToThisGroup(
  jobAssignmentToSearch: JobAssignmentDoc,
  jobAssignmentsAtGroup: JobAssignmentDoc[]
) {
  return jobAssignmentsAtGroup.find(
    (jobAssignmentDoc) =>
      jobAssignmentDoc.get('receiver.user.id') ===
      jobAssignmentToSearch.get('receiver.user.id')
  );
}

function JobAssignmentsGroup({
  jobAssignmentsAtGroup,
  groupIndex,
  jobDoc,
  groupsCount,
  canOperatorsBeAdded,
  originalJobAssignments,
}: {
  jobAssignmentsAtGroup: JobAssignmentDoc[];
  groupIndex: number;
  jobDoc: JobDoc;
  groupsCount: number;
  canOperatorsBeAdded?: boolean;
  originalJobAssignments: JobAssignmentDoc[];
}) {
  const {
    onSelectOperatorToMove,
    selectedGroup,
    selectedJob,
    isConfirmChangesDisabled,
  } = useEditSchedulerContext();
  // Generate a list of the groups that this group can receive
  const acceptedGroups = compact(
    times(
      groupsCount,
      (group) => group !== +groupIndex && `job-${jobDoc.id}-group-${group}`
    )
  );

  const onDropOperator = ({
    jobAssignmentDoc,
  }: {
    jobAssignmentDoc: JobAssignmentDoc;
  }) => {
    // Check that the same user (possibly an Assigner) is not already assigned to a different group
    if (
      getIsSameUserAssignedToThisGroup(jobAssignmentDoc, jobAssignmentsAtGroup)
    )
      return;
    onSelectOperatorToMove(jobAssignmentDoc, +groupIndex);
  };

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: acceptedGroups,
    drop: onDropOperator,
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop:
        monitor.canDrop() &&
        !getIsSameUserAssignedToThisGroup(
          monitor.getItem().jobAssignmentDoc,
          jobAssignmentsAtGroup
        ),
    }),
  });

  const jobAssignmentsSorted: JobAssignmentDoc[] = orderBy(
    jobAssignmentsAtGroup,
    (jobAssignmentDoc) =>
      jobAssignmentDoc.get('receiver.company.name')?.toLowerCase()
  );

  const isGroupInInvalidState =
    (jobDoc.id === selectedJob?.id &&
      groupIndex === selectedGroup &&
      isConfirmChangesDisabled) ||
    // Ensure that job has root JobAssignment to be able
    // to assign operators through dispatch scheduler
    !originalJobAssignments.length;

  return (
    <>
      {+groupIndex > 0 && (
        <div className="job-start-time text-base-100 line-clamp-1 px-2 py-2 text-right">
          {getJobStartDatetime(jobDoc, +groupIndex).getTimeStr()}
        </div>
      )}
      <div
        // @ts-ignore
        ref={drop}
        className={clsx(
          'job-assignments-group bg-base-100 relative rounded-xl pt-0',
          selectedGroup === null ? 'p-2' : 'px-1 pb-2'
        )}
        key={groupIndex}
      >
        {canDrop && <DropPlaceholderOverlay isOver={isOver} />}
        <div className="group mb-0.5 flex h-6 items-center justify-between text-xs font-light italic underline">
          <div className="flex">
            Operators:
            {isGroupInInvalidState && (
              <div
                className="tooltip tooltip-right before:top-6! before:max-w-48 before:text-left before:text-xs"
                data-tip={
                  !originalJobAssignments.length
                    ? 'No rates have been established for this Job yet. Complete the assignments info through Job Form.'
                    : 'Complete all pending assignment slots before removing yourself from this group.'
                }
              >
                <ExclamationTriangleIcon className="text-danger size-4" />
              </div>
            )}
            <AddOperatorsButton
              disabled={!canOperatorsBeAdded}
              jobDoc={jobDoc}
              groupIndex={+groupIndex}
              jobAssignments={originalJobAssignments}
            />
          </div>
          {groupsCount > 1 && <div>{`Group ${+groupIndex + 1}`}</div>}
        </div>
        {!!jobAssignmentsSorted.length ? (
          jobAssignmentsSorted.map((jobAssignmentDoc) => (
            <JobAssignmentInfo
              key={jobAssignmentDoc.id}
              jobAssignmentDoc={jobAssignmentDoc}
              jobDoc={jobDoc}
            />
          ))
        ) : (
          <div className="w-full py-1 text-center text-xs">
            {jobDoc.get('overallStatus') === 'INCOMPLETE'
              ? 'Operators cannot be assigned while Job is incomplete'
              : 'No Operators have been assigned to this Group'}
          </div>
        )}
      </div>
    </>
  );
}

function AddOperatorsButton({
  jobDoc,
  jobAssignments,
  groupIndex,
  disabled,
}: {
  jobDoc: JobDoc;
  jobAssignments: JobAssignmentDoc[];
  groupIndex: number;
  disabled?: boolean;
}) {
  const { userDoc } = useAuthContext();
  const {
    canUserEditDispatchSchedule,
    selectedJob,
    onSelectGroup,
    selectedGroup,
    onSelectJob,
    onChangeParentJobAssignment,
  } = useEditSchedulerContext();

  const onClickAddOperator = () => {
    // If no root Job Assignment is present
    // it should then redirect to Job Form
    if (!jobAssignments.length) {
      return onNavigateToJobForm(jobDoc);
    }
    if (!selectedJob) {
      onSelectJob(jobDoc);
    }
    onSelectGroup(isSelectedGroup ? null : groupIndex);
    if (!userDoc) return;
    onChangeParentJobAssignment(
      findParentJobAssignment({
        jobAssignments,
        groupIndex,
        userDoc,
      })
    );
  };

  const isSelectedJob = selectedJob?.id === jobDoc.id;
  const isSelectedGroup = selectedGroup === groupIndex;

  const canManageAssignments =
    canUserEditDispatchSchedule &&
    !!userDoc &&
    getCanUserManageJobAssignmentsGroup({
      jobDoc,
      userDoc,
      jobAssignments,
      groupIndex,
    });

  return (
    canManageAssignments && (
      <div
        className={clsx(
          'add-operators-btn btn btn-outline rounded-selector btn-secondary btn-xs ml-2 line-clamp-1 h-4 min-h-4',
          'transition-[width] duration-200',
          'justify-start',
          isSelectedJob && isSelectedGroup && 'btn-active',
          !isSelectedJob ? 'w-8 group-hover:w-44' : 'w-44',
          'hidden md:inline-flex', // do not show button in mobile view
          disabled && 'btn-disabled' // btn-disabled prevents onClick from being fired
        )}
        onClick={onClickAddOperator}
      >
        <span className="">
          <UserPlusIcon className="inline size-4" />
        </span>
        <span
          className={clsx(!isSelectedJob && 'invisible group-hover:visible')}
        >
          Manage Assignments
        </span>
      </div>
    )
  );
}

function DropPlaceholderOverlay({ isOver }: { isOver: boolean }) {
  return (
    <div
      className={clsx(
        'absolute left-0 z-20 mr-5 flex h-full w-full items-center justify-center rounded-lg',
        isOver ? 'bg-cyan-400' : 'bg-info'
      )}
    >
      <div className="text-base-100 font-bold">
        {'Move Operator to this group'}
      </div>
    </div>
  );
}
